import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import useOutsideClick from "../../hooks/useOutsideClick";
import { api } from "../../actions/api";
import { toast } from "react-toastify";
import AddNewTaskModal from "../AddNewTaskModel";

const TaskMenu = ({ onClose, onMove, taskId, taskData, onUpdate }) => {
  // Add taskData prop
  const [showDeleteSubmenu, setShowDeleteSubmenu] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false); // State for edit modal
  const [currentTaskId, setCurrentTaskId] = useState(taskId); // Track current task ID

  const tasd = taskData;


  const menuRef = useRef(null);

  useEffect(() => {
    setCurrentTaskId(taskId);
  }, [taskId]);

  const getCurrentColumn = (taskId) => {
    if (taskId.startsWith("hit-")) return "hitList";
    if (taskId.startsWith("mit-")) return "mitList";
    if (taskId.startsWith("do-")) return "doList";
    if (taskId.startsWith("done-")) return "doneList";
    if (taskId.startsWith("ach-")) return "achievedList";
    if (taskId.startsWith("done1-only-")) return "doneList";
    if (taskId.startsWith("achieved-only-")) return "achievedList";
    return null;
  };

  // Helper function to determine if task is greyed out
  const isTaskGreyedOut = (taskId, taskData) => {
    // Check if task is in MIT column but has achieved status
    if (
      taskId.startsWith("mit-achieved-") ||
      (taskId.startsWith("mit-") && taskData?.status === "Achieved")
    ) {
      return true;
    }
    // Check if task is in DO column but has done status
    if (
      taskId.startsWith("do-done-") ||
      (taskId.startsWith("do-") && taskData?.status === "Done")
    ) {
      return true;
    }
    return false;
  };

  const isValidMove = (sourceColumnId, taskId, destinationColumnId) => {
    if (
      sourceColumnId === "hitList" &&
      destinationColumnId === "completedList"
    ) {
      return false;
    }

    if (
      (sourceColumnId === "mitList" && destinationColumnId === "doList") ||
      (sourceColumnId === "doList" && destinationColumnId === "mitList")
    ) {
      return false;
    }

    if (
      sourceColumnId === "completedList" &&
      destinationColumnId !== "completedList" &&
      taskId.startsWith("done-")
    ) {
      return destinationColumnId === "doList";
    }

    if (
      sourceColumnId === "completedList" &&
      destinationColumnId !== "completedList" &&
      taskId.startsWith("ach-")
    ) {
      return destinationColumnId === "mitList";
    }

    if (sourceColumnId === "mitList") {
      return (
        destinationColumnId === "achievedList" ||
        destinationColumnId === "hitList"
      );
    }

    if (sourceColumnId === "doList") {
      return (
        destinationColumnId === "doneList" || destinationColumnId === "hitList"
      );
    }

    if (sourceColumnId === "hitList") {
      return (
        destinationColumnId === "mitList" || destinationColumnId === "doList"
      );
    }

    if (sourceColumnId === "achievedList") {
      return destinationColumnId === "mitList";
    }

    if (sourceColumnId === "doneList") {
      return destinationColumnId === "doList";
    }

    if (sourceColumnId === destinationColumnId) {
      return true;
    }

    return false;
  };

  const currentColumn = getCurrentColumn(currentTaskId);

  const allColumnOptions = [
    { label: "HIT List", id: "hitList" },
    { label: "MIT List", id: "mitList" },
    { label: "Do List", id: "doList" },
    { label: "Achieved List", id: "achievedList" },
    { label: "Done List", id: "doneList" },
  ];

  const columnOptions = allColumnOptions.filter((col) => {
    if (col.id === currentColumn) return false;

    return isValidMove(currentColumn, taskId, col.id);
  });

  const handleMove = (destination) => {
    onMove(destination);
    onClose();
  };

  // delete task with scope
  const handleDelete = async (deleteScope = null) => {
    if (!taskId) {
      console.error("Task ID is missing");
      toast.error("Task ID is missing. Cannot delete.");
      return;
    }

    try {
      const id = taskId.includes("-") ? taskId.split("-")[1] : taskId;

      // Prepare query parameters based on delete scope
      let queryParams = "";
      if (deleteScope === "all") {
        queryParams = "?delete_all=true";
      } else if (deleteScope === "this_and_future") {
        queryParams = "?delete_this_and_future=true";
      } else if (deleteScope === "this_only") {
        queryParams = "?delete_this_only=true";
      }

      const response = await api.delete(`/tasks/${id}${queryParams}`);

      if (response.data.status) {
        toast.success("Task deleted successfully!");
        onClose();
        onUpdate?.();
      } else {
        toast.error(response.data.message || "Failed to delete task");
      }
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error(error.response?.data?.message || "Failed to delete task");
    } finally {
      setShowDeleteSubmenu(false);
      onClose();
    }
  };

  // Edit task handler
  const handleEdit = () => {
    setShowEditModal(true);
    // onClose();
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    onClose();
  };

  return (
    <>
      <MenuContainer ref={menuRef} onClick={(e) => e.stopPropagation()}>
        <div className="kanban-task-menu-header">
          <h4
            className="text-body-b2 mb-0"
            style={{ color: "#e6ecf1", cursor: "pointer" }}
            onClick={handleEdit}
          >
            Edit Task
          </h4>
          <div className="position-relative">
            <h4
              className="text-body-b2 mb-0 mt-2"
              style={{ color: "#e6ecf1", cursor: "pointer" }}
              onClick={() => setShowDeleteSubmenu(!showDeleteSubmenu)}
            >
              Delete Task
            </h4>
            {showDeleteSubmenu && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: "0",
                  backgroundColor: "#192f41",
                  border: "1px solid #2d3c48",
                  borderRadius: "8px",
                  padding: "8px 0",
                  marginTop: "4px",
                  zIndex: 1000,
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                  minWidth: "200px",
                }}
              >
                {taskData?.is_reoccuring ? (
                  <>
                    <button
                      className="btn w-100 text-start px-3 py-2"
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#e6ecf1",
                        fontSize: "14px",
                      }}
                      onClick={() => handleDelete("this_only")}
                    >
                      Delete This Only
                    </button>
                    <button
                      className="btn w-100 text-start px-3 py-2"
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#e6ecf1",
                        fontSize: "14px",
                      }}
                      onClick={() => handleDelete("this_and_future")}
                    >
                      Delete This and Future
                    </button>
                    <button
                      className="btn w-100 text-start px-3 py-2"
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#eb5757",
                        fontSize: "14px",
                      }}
                      onClick={() => handleDelete("all")}
                    >
                      Delete All
                    </button>
                  </>
                ) : (
                  <button
                    className="btn w-100 text-start px-3 py-2"
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#e6ecf1",
                      fontSize: "14px",
                    }}
                    onClick={() => handleDelete()}
                  >
                    Delete Task
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Only show move section if there are valid options */}
        {columnOptions.length > 0 && (
          <>
            <hr className="task-menu-hr mb-3" />
            <div
              className="d-flex flex-column gap-3"
              style={{ paddingLeft: "20px", marginBottom: "22px" }}
            >
              <span className="text-body-b2" style={{ color: "#008cff" }}>
                Move to:
              </span>
              {columnOptions.map((col) => (
                <span
                  key={col.id}
                  className="text-body-b2"
                  style={{ color: "#e6ecf1", cursor: "pointer" }}
                  onClick={() => handleMove(col.id)}
                >
                  {col.label}
                </span>
              ))}
            </div>
          </>
        )}
      </MenuContainer>

      {/* Edit Task Modal */}
      {showEditModal && (
        <AddNewTaskModal
          show={showEditModal}
          onClose={handleCloseEditModal}
          taskId={taskId}
          taskData={taskData}
          isEditMode={true}
          onUpdate={onUpdate}
        />
      )}
    </>
  );
};

const MenuContainer = styled.div`
  position: absolute;
  right: 16px;
  top: 50px;
  background: #121e29;
  border-radius: 8px;
  padding: 16px;
  width: 200px;
  z-index: 10;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);

  .task-menu-hr {
    border-color: rgba(255, 255, 255, 0.1);
  }
`;

export default TaskMenu;
